import { ChangeEvent, useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { set, snakeCase } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import { selectionSetStyle } from 'common/styles'
import CheckboxGroupRows from 'elements/CheckboxGroupSet/components/CheckboxGroupRow'
import validationsToMapRules from 'utils/validationsToRules'

import { CheckboxGroupSet as CheckboxGroupSetProps } from './interface'

export default function CheckboxGroupSet({
  name,
  validations,
  variants,
  labels,
  label,
  onChange,
  onFocus,
  defaultValue,
}: CheckboxGroupSetProps & DefaultElementProps): JSX.Element {
  const { width100, variantText, variantTextWrap } = selectionSetStyle

  const defaultValues = useMemo(() => {
    if (typeof defaultValue === 'string' && variants.includes(defaultValue)) {
      return labels.reduce((acc: Record<string, string[]>, currentValue) => {
        const checkboxGroupName = snakeCase(currentValue)
        acc[checkboxGroupName] = [defaultValue]
        return acc
      }, {})
    }

    return defaultValue
  }, [])

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: control,
    defaultValue: defaultValues ?? {},
    rules: validationsToMapRules(validations),
  })

  function handleChange(event: ChangeEvent<HTMLInputElement>, variant: string): void {
    const checkboxGroupName = event.target.name
    const newValue = toggleValue(field.value[checkboxGroupName], variant)
    const updatedValue = set(field.value, checkboxGroupName, newValue)
    field.onChange(updatedValue)
    onChange?.({ value: updatedValue })
  }

  function toggleValue(array: string[] = [], value: string): string[] {
    const newArray = [...array]
    const index = newArray.indexOf(value)
    index === -1 ? newArray.push(value) : newArray.splice(index, 1)
    return newArray
  }

  return (
    <FormControl
      className="checkbox-set-wrap"
      onFocus={onFocus}
      error={error !== undefined}
      sx={width100}
    >
      {label !== undefined && <FormLabel>{label}</FormLabel>}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={width100} />
            <TableCell sx={variantTextWrap}>
              {variants.map((variant) => (
                <Typography variant="body2" sx={variantText} key={variant}>
                  {variant}
                </Typography>
              ))}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {labels.map((item) => (
            <CheckboxGroupRows
              item={item}
              variants={variants}
              value={field.value}
              handleChange={handleChange}
              key={item}
            />
          ))}
        </TableBody>
      </Table>
      {error !== undefined && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  )
}
